import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderGroupParams,
  FilterOptionsState,
  SxProps,
} from "@mui/material";
import { HTMLAttributes, ReactNode, SyntheticEvent } from "react";
import CommonTextField from "../../commonTextField/CommonTextField";

export type AutocompleteBaseSingleProps<T> = {
  value: T | null;
  options: readonly T[];
  onChange: (value: T | null) => void;
  getOptionLabel?: (option: T) => string;
  getOptionDisabled?: (option: T) => boolean;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  name?: string;
  sx?: SxProps;
  placeholder?: string;
  noOptionsText?: ReactNode;
  loading?: boolean;
  loadingText?: ReactNode;
  disableClearable?: boolean;
  inputValue?: string;
  onClose?: () => void;
  onInputChange?: (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  groupBy?: (option: T) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode;
  renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: T) => ReactNode;
  helperText?: string;
};

export const AutocompleteBaseSingle = <T,>({
  value,
  onChange,
  options,
  getOptionLabel,
  getOptionDisabled,
  disabled,
  error,
  required,
  name,
  sx,
  placeholder,
  noOptionsText,
  loading,
  loadingText,
  disableClearable,
  inputValue,
  onClose,
  onInputChange,
  filterOptions,
  groupBy,
  renderGroup,
  renderOption,
  isOptionEqualToValue,
  helperText,
}: AutocompleteBaseSingleProps<T>) => {
  return (
    <Autocomplete<T, false, boolean, false>
      fullWidth
      multiple={false}
      value={value || null}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getOptionDisabled}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={(_, newValue) => {
        onChange(newValue ? (newValue as T) : null);
      }}
      disabled={disabled}
      loading={loading}
      loadingText={loadingText}
      disableClearable={disableClearable}
      inputValue={inputValue}
      onClose={onClose}
      onInputChange={onInputChange}
      filterOptions={filterOptions}
      groupBy={groupBy}
      renderGroup={renderGroup}
      renderOption={renderOption}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <CommonTextField
          {...params}
          name={name}
          error={error}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          helperText={helperText}
        />
      )}
      sx={{
        // workaround to prevent component to resize on hover/focus -> updating the MUI package should fix this
        "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
          { minWidth: "30px" },
        ...sx,
      }}
    />
  );
};
